import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonListHeader,
  IonPage,
  IonRippleEffect,
  IonSearchbar,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
} from '@ionic/react'
import { IOption, IOptions } from '../../common/common.types'
import { checkmark, chevronBackOutline } from 'ionicons/icons'
import useDeviceMetrics from '../../utilities/useDeviceMetrics'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getSearchStateSelector } from '../search/selectors'
import './SingleSelectPage.component.css'
import PCRHeader from '../pcrHeader/PCRHeader.component'
import { Virtuoso } from 'react-virtuoso'

interface ISingleSelectPageProps {
  listHeader?: string
  dividerText?: string
  options: any
  commonOptions?: IOptions
  selectedOption?: IOption | null
  dispatchData?: any
  action: (e: BaseSyntheticEvent, option: any, dispatchData?: any) => void
  backAction: (e: BaseSyntheticEvent) => void
  loading: boolean
  hidePageTag?: boolean
  showHeader?: boolean
  isAlphabeticalList?: boolean
}

const SingleSelectPage: React.FC<ISingleSelectPageProps> = (props: ISingleSelectPageProps) => {
  const { t } = useTranslation()
  const {
    options,
    action,
    dispatchData,
    backAction,
    listHeader,
    dividerText,
    selectedOption,
    loading,
    commonOptions,
    hidePageTag,
    showHeader = true,
    isAlphabeticalList = true,
  } = props

  const { isTablet } = useDeviceMetrics()
  const search = useSelector(getSearchStateSelector)
  const { isLoading } = search
  const [searchValue, setSearchValue] = useState('')
  const [optionsList, setOptionsList] = useState<any>([])
  const [commonOptionsList, setCommonOptionsList] = useState<IOptions>([])
  const [searchResult, setSearchResult] = useState<IOptions>([])
  const [optionsLoaded, setOptionsLoaded] = useState(false)

  const initializeComponent = () => {
    const initialOption = options.slice(0, 20)
    if (commonOptions) {
      setOptionsList([...commonOptions, ...initialOption])
      setCommonOptionsList([...commonOptions])
    } else setOptionsList([...initialOption])
  }

  useIonViewDidLeave(() => {
    setSearchValue('')
  })

  useEffect(() => {
    setOptionsLoaded(true)
    initializeComponent()
    setOptionsLoaded(false)
  }, [])

  useEffect(() => {
    initializeComponent()
  }, [options])

  const handleSearch = (e: CustomEvent) => {
    setSearchValue(e.detail.value)
    if (e.detail.value === '') {
      const initialOption = options.slice(0, 20)
      if (commonOptions) {
        setCommonOptionsList([...commonOptions])
        setOptionsList([...commonOptions, ...initialOption])
      } else setOptionsList([...initialOption])
      setSearchResult([])
    } else {
      let filteredCommonOptionsList: IOptions = []
      if (commonOptions) {
        filteredCommonOptionsList = commonOptions.filter((o: IOption) =>
          o.name?.toLowerCase().includes(e.detail.value.toLowerCase().trim())
        )
        setCommonOptionsList(filteredCommonOptionsList)
      }
      const filteredOptionsList = options.filter((o: IOption) =>
        o.name?.toLowerCase().includes(e.detail.value.toLowerCase().trim())
      )
      setOptionsList([...filteredCommonOptionsList, ...filteredOptionsList.slice(0, 20)])
      setSearchResult([...filteredCommonOptionsList, ...filteredOptionsList])
    }
  }

  const selectOption = (e: BaseSyntheticEvent, option: any) => {
    action(e, option, dispatchData)
  }

  const loadData = () => {
    setTimeout(() => {
      if (searchValue === '') {
        if (options.length > 20 && optionsList.length < options.length) {
          const totalLength = optionsList.length - commonOptionsList.length
          const filteredChunkOption = options.slice(totalLength, totalLength + 20)
          setOptionsList([...optionsList, ...filteredChunkOption])
        }
      } else {
        if (searchResult.length > 20 && optionsList.length < searchResult.length) {
          const filteredChunkOption = searchResult.slice(optionsList.length, optionsList.length + 20)
          setOptionsList([...optionsList, ...filteredChunkOption])
        }
      }
    }, 200)
  }

  useEffect(() => {
    if (searchValue) {
      let content: HTMLIonContentElement | null = document.querySelector(':not(.ion-page-hidden) > ion-content')
      content?.scrollToTop(200)
      content = null
    }
  }, [searchValue])
  const onBackAction = (e: BaseSyntheticEvent) => {
    backAction(e)
  }

  const singleSelectPageList = (
    <>
      {!optionsList.length ? (
        <>
          <IonListHeader color='light'>
            <IonTitle className='item-header'>{t('common.AtoZ')}</IonTitle>
          </IonListHeader>
          <IonItem data-testid='NoOptions' key={'no_options_found'}>
            <IonLabel>
              {t('common.NoSearchResults')}
              {`"${searchValue}"`}
            </IonLabel>
          </IonItem>
        </>
      ) : (
        <Virtuoso
          style={{ height: isAlphabeticalList ? '90%' : isTablet ? 'calc(100% - 60px)' : '100%' }}
          data={optionsList}
          components={{
            Header: () => {
              return (
                <>
                  {commonOptionsList.length > 0 && (
                    <IonListHeader color='light'>
                      <IonTitle className='item-header'>{t('common.Common')}</IonTitle>
                    </IonListHeader>
                  )}
                </>
              )
            },
          }}
          endReached={loadData}
          increaseViewportBy={{ top: 2000, bottom: 2000 }}
          overscan={10}
          itemContent={(index, option: any) => {
            return (
              <div style={{ minHeight: '44px' }} className='webkit-line-clamp-2'>
                {index === commonOptionsList.length && isAlphabeticalList && (
                  <IonListHeader color='light'>
                    <IonTitle className='item-header'>{t('common.AtoZ')}</IonTitle>
                  </IonListHeader>
                )}
                <IonItem
                  button
                  detail={false}
                  data-testid={`Option_${option.id}`}
                  key={`option_${option.id}`}
                  onClick={(e) => selectOption(e, option)}
                  className='ion-no-padding ion-activatable'>
                  <IonLabel className='label-left-padding'>{option.name}</IonLabel>
                  <IonRippleEffect />
                  {selectedOption && selectedOption.name === option.name && (
                    <IonIcon className='ion-no-margin' color='primary-alt' icon={checkmark} />
                  )}
                </IonItem>
              </div>
            )
          }}
        />
      )}
    </>
  )

  const singleSelectPageContent = (
    <>
      {showHeader && <PCRHeader />}
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='BackSingleSelect' color='primary-alt' onClick={(e) => onBackAction(e)}>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          {listHeader && <IonTitle data-testid='HeaderTitle'>{listHeader}</IonTitle>}
        </IonToolbar>
        <IonToolbar color='light'>
          <IonSearchbar
            data-testid='SearchBar'
            debounce={0}
            value={searchValue}
            onIonChange={handleSearch}
            autocomplete='off'></IonSearchbar>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {dividerText && (
          <IonItemDivider color='light' className={isTablet ? 'ion-padding' : 'ion-hide-sm-down'}>
            <IonLabel className='grid-item-divider-label' color='medium'>
              {dividerText}
            </IonLabel>
          </IonItemDivider>
        )}
        {(isLoading || loading || optionsLoaded) && <IonSpinner data-testid='Spinner' />}
        {!loading && options && singleSelectPageList}
      </IonContent>
    </>
  )

  return (
    <>
      {!hidePageTag && <IonPage id='singleSelectPage'>{singleSelectPageContent}</IonPage>}
      {hidePageTag && singleSelectPageContent}
    </>
  )
}

export default SingleSelectPage
